import { useMutation } from '@apollo/client';
import type { InsertAttestationCycleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertAttestationCycleDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import {
  type InsertAttestationCycleInput,
  useInsertAttestationCycleTRPC,
} from './useInsertAttestationCycleTRPC';

/**
 * Flag-gated wrapper for the `insertAttestationCycle` mutation. Defaults to the
 * legacy Apollo mutation and delegates to the tRPC variant when the `trpc`
 * feature flag is enabled. The public input shape matches the tRPC
 * `InsertAttestationCycleInput`; the Apollo path maps it to the legacy
 * `{ DocumentId, AllowCarryForward, attestationConfig }` variables.
 */
export const useInsertAttestationCycle = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertAttestationCycleDocument
  );

  const trpcMutation = useInsertAttestationCycleTRPC();

  const insertAttestationCycle = async (
    input: InsertAttestationCycleInput
  ): Promise<InsertAttestationCycleMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertAttestationCycle(input);
    }

    const result = await insertGraphQL({
      variables: {
        DocumentId: input.DocumentId,
        AllowCarryForward: input.AllowCarryForward,
        attestationConfig: input.AttestationConfig
          ? {
              ParentId: input.AttestationConfig.ParentId,
              RequireGlobalAttestation:
                input.AttestationConfig.RequireGlobalAttestation,
              AttestationGroupIds: input.AttestationConfig.AttestationGroupIds,
              AttestationPromptText:
                input.AttestationConfig.AttestationPromptText,
              AttestationTimeLimit:
                input.AttestationConfig.AttestationTimeLimit,
            }
          : undefined,
      },
    });
    if (!result.data) {
      throw new Error('Failed to save attestation cycle');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertAttestationCycle,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertAttestationCycle,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
